/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {BUILD_VERSION, METRICS_VERSION} from './BuildVersion';
import type Configuration from './Configuration';
import initAnalytics from './AnalyticsScript';
import {initialize} from './AdobeMetricsRuntime';
import initPerformance from './Performance';
import {Level, RecordType} from '@exc/metrics';
import MetricsInteractionIntercept from './MetricsInteractionIntercept';
import MetricsNetworkIntercept from './MetricsNetworkIntercept';
import {onCLS, onFCP, onINP, onLCP, onTTFB, Metric as WebVitalsMetric} from 'web-vitals';

export const LOGGER_NAME = 'exc.metrics-browser-runtime.MetricsBrowserRuntime';

/**
 * Reports Webvitals to passed callback.
 *
 * @param {object} onPerfEntry Function to handle webVital callback.
 */
export function reportWebVitals(onPerfEntry: (vital: WebVitalsMetric) => void): void {
  onCLS(onPerfEntry);
  onINP(onPerfEntry);
  onFCP(onPerfEntry);
  onLCP(onPerfEntry);
  onTTFB(onPerfEntry);
}

const setCallbacks = (win: Window): void => {
  const {adobeMetrics, navigator} = win;
  /**
   * Global callback that handles uncaught errors and promise rejections.
   *
   * @param {ErrorEvent} event Holds error information.
   */
  function errorEventCallback(event: ErrorEvent): void {
    adobeMetrics.log(
      RecordType.LOG,
      Level.ERROR,
      LOGGER_NAME,
      event.message,
      'uncaughtException',
      {sdkVersion: METRICS_VERSION},
      Object.assign({}, event, {
        colno: event.colno,
        error:
          event.error &&
          Object.assign({}, event.error, {
            message: event.error.message,
            stack: event.error.stack,
          }),
        filename: event.filename,
        lineno: event.lineno,
        message: event.message,
        timeStamp: event.timeStamp,
        type: event.type,
      }),
    );
  }

  /**
   * Global callback that handles uncaught errors and promise rejections.
   *
   * @param {ErrorEvent | PromiseRejectionEvent} event Holds error information.
   */
  function promiseRejectionCallback(event: PromiseRejectionEvent): void {
    if (!event) {
      return;
    }
    const reason =
      typeof event.reason == 'string'
        ? {message: event.reason || 'Unknown reason'}
        : {...event.reason, message: event.reason?.message || 'Unknown reason', stack: event.reason?.stack};
    adobeMetrics.log(
      RecordType.LOG,
      Level.ERROR,
      LOGGER_NAME,
      reason.message,
      'uncaughtPromiseRejection',
      {sdkVersion: METRICS_VERSION},
      Object.assign({}, event, {
        reason,
        timeStamp: event.timeStamp,
        type: event.type,
      }),
    );
  }
  /**
   * Global callback that handles security violations.
   *
   * @param {SecurityPolicyViolationEvent} event Holds security violation information.
   */
  function securityViolationsCallback(event: SecurityPolicyViolationEvent): void {
    const {
      blockedURI,
      columnNumber,
      disposition,
      effectiveDirective,
      lineNumber,
      originalPolicy,
      referrer,
      sourceFile,
      statusCode,
      violatedDirective,
    } = event;
    const payload = {
      ...event,
      blockedURI,
      columnNumber,
      disposition,
      effectiveDirective,
      lineNumber,
      originalPolicy,
      referrer,
      sourceFile,
      statusCode,
      timeStamp: event.timeStamp,
      type: event.type,
      violatedDirective,
    };
    disposition === 'enforce' &&
      adobeMetrics.log(
        RecordType.LOG,
        Level.ERROR,
        LOGGER_NAME,
        `${violatedDirective} directive security violation`,
        'securityPolicyViolation',
        {sdkVersion: METRICS_VERSION},
        payload,
      );
    disposition === 'report' &&
      adobeMetrics.log(
        RecordType.LOG,
        Level.WARN,
        LOGGER_NAME,
        `${violatedDirective} directive security violation - report only`,
        'securityPolicyViolationReportOnly',
        {sdkVersion: METRICS_VERSION},
        payload,
      );
  }
  /**
   * Global callback that handles network offline/online status changes.
   */
  function onlineStatusChangeCallback(): void {
    if (navigator.onLine) {
      adobeMetrics.log(RecordType.LOG, Level.INFO, LOGGER_NAME, 'Network is back online.', 'networkOnline', {
        sdkVersion: METRICS_VERSION,
      });
    } else {
      adobeMetrics.log(RecordType.LOG, Level.WARN, LOGGER_NAME, 'Network has gone offline.', 'networkOffline', {
        sdkVersion: METRICS_VERSION,
      });
    }
  }

  reportWebVitals((vital) => {
    const {name, navigationType, rating, value} = vital;
    adobeMetrics.log(RecordType.LOG, Level.INFO, LOGGER_NAME, undefined, `WebVitals.${name}`, {
      navigationType,
      rating,
      value,
    });
  });

  adobeMetrics.init.runtime = {
    errorEventCallback,
    onlineStatusChangeCallback,
    promiseRejectionCallback,
    securityViolationsCallback,
  };
  win.addEventListener('error', errorEventCallback, false);
  win.addEventListener('unhandledrejection', promiseRejectionCallback, false);
  win.addEventListener('online', onlineStatusChangeCallback, false);
  win.addEventListener('offline', onlineStatusChangeCallback, false);
  win.addEventListener('securitypolicyviolation', securityViolationsCallback, false);
};

/**
 * Initialize the MetricsRuntime and the Metrics package. This
 * performs various tasks to add instrumentation to network calls
 * and start background monitors.
 *
 * @param {object} config A configuration object for the desired state.
 * @param {Window} g - Window/Global.
 * @returns {Configuration} The resulting configuration.
 */
// eslint-disable-next-line no-restricted-globals
function configure(config?: Configuration, g: Window & typeof globalThis = global as any): Configuration {
  initialize(g);
  const a = g.adobeMetrics;
  a.metricsState.window = {...a.metricsState.window, iframe: g.parent !== g};
  config && a.configure(config);
  if (!a.init.runtime && 'addEventListener' in g) {
    setCallbacks(g);
    MetricsInteractionIntercept.init(a, g, config?.mode);
  }
  MetricsNetworkIntercept.init(a, g, config?.mode);
  initPerformance(a, g);
  initAnalytics(a, g, config?.analytics);
  return a.configure();
}

const MetricsRuntime = {
  configure,
  initialize, // initialize is basically a default configure
  version: BUILD_VERSION,
};
export default MetricsRuntime;
